import { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Card, CardContent } from '@mui/material';
import { useAwsGetter, useRoutes } from '@localstack/services';
import { LambdaWebEndpointDetails, LoadingFragment, PageTitle } from '@localstack/ui';

import { LambdaProps } from './types';
import { LambdaWebVersionGate } from './components/LambdaWebVersionGate';
import { WebFunctionTabs } from './components/WebFunctionTabs';

export const LambdaWebEndpoint = (props: LambdaProps): ReactElement => (
  <LambdaWebVersionGate {...props}>
    <LambdaWebEndpointContent {...props} />
  </LambdaWebVersionGate>
);

const LambdaWebEndpointContent = ({ Layout, clientOverrides, routes }: LambdaProps): ReactElement => {
  const { name = '', endpointName = '' } = useParams<'name' | 'endpointName'>();
  const { goto } = useRoutes();
  const { data, isLoading, mutate } = useAwsGetter(
    'LambdaWeb',
    'getWebFunctionEndpoint',
    [{ functionName: name, endpointName }],
    { clientOverrides },
  );
  return (
    <Layout
      documentTitle="Lambda: Endpoint Details"
      tabs={<WebFunctionTabs routes={routes} functionName={name} clientOverrides={clientOverrides} />}
      title={
        <PageTitle
          title="Endpoint Details"
          onMutate={mutate}
          breadcrumbs={[
            ['Lambda', () => goto(routes.RESOURCES_LAMBDA)],
            ['Web Functions', () => goto(routes.RESOURCES_LAMBDA_WEB_FUNCTIONS)],
            [name, () => goto(routes.RESOURCES_LAMBDA_WEB_FUNCTION, { name })],
            ['Endpoints', () => goto(routes.RESOURCES_LAMBDA_WEB_ENDPOINTS, { name })],
            [endpointName, null],
          ]}
        />
      }
      actions={
        <Button
          variant="outlined"
          onClick={() => goto(routes.RESOURCES_LAMBDA_WEB_ENDPOINT_UPDATE, { name, endpointName })}
        >
          Edit
        </Button>
      }
    >
      <Card>
        <CardContent>
          <LoadingFragment loading={isLoading} variant="list" height={50} size={6}>
            <LambdaWebEndpointDetails data={data} />
          </LoadingFragment>
        </CardContent>
      </Card>
    </Layout>
  );
};
